import type { FunctionArgs } from '@vueuse/core'

// Streamlined UX delay utilities for TwinePOS frontend
// Standard UX delay range: 300ms to 800ms (0.3s to 0.8s) for optimal perceived performance
import { useDebounceFn } from '@vueuse/core'

/**
 * Standard UX delay range: 300ms to 800ms (0.3s to 0.8s)
 * This range provides optimal perceived performance and smooth UX transitions
 */
const UX_DELAY_MIN = 300
const UX_DELAY_MAX = 800
const STANDARD_FIXED_DELAY = 300

const DIALOG_FOCUS_DELAY_MS = 250

/**
 * Generates a random delay within the standard UX range (300-800ms)
 * @param min - Minimum delay in milliseconds (default: 300)
 * @param max - Maximum delay in milliseconds (default: 800)
 * @returns Random delay in milliseconds within the specified range
 */
const generateRandomDelay = (min: number = UX_DELAY_MIN, max: number = UX_DELAY_MAX): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

/**
 * Creates a debounced function with fixed 300ms delay
 * Consistent timing for predictable UX interactions
 * @param callback - Function to debounce
 */
const debounceWithFixedDelay = <T extends FunctionArgs>(callback: T) => {
  return useDebounceFn(callback, STANDARD_FIXED_DELAY)
}

/**
 * Creates a Promise that resolves after the specified delay
 * @param ms - Milliseconds to delay (default: 300ms)
 */
const sleep = (ms: number = STANDARD_FIXED_DELAY): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Ensures an async operation takes at least a minimum duration
 * Prevents loading state flicker when operations complete too quickly
 * @param operation - The async operation to execute
 * @param minDuration - Minimum duration in milliseconds or 'random' for 300-800ms range
 */
const withMinimumDelay = async <T>(
  operation: () => Promise<T>,
  minDuration: number | 'random' = STANDARD_FIXED_DELAY
): Promise<T> => {
  const delay = minDuration === 'random' ? generateRandomDelay() : minDuration

  const startTime = Date.now()
  const result = await operation()
  const elapsedTime = Date.now() - startTime

  if (elapsedTime < delay) {
    await sleep(delay - elapsedTime)
  }

  return result
}

/**
 * Async operation with random 300-800ms minimum delay
 * @param operation - The async operation to execute
 */
const withRandomMinimumDelay = async <T>(operation: () => Promise<T>): Promise<T> => {
  return withMinimumDelay(operation, 'random')
}

// Export streamlined API
export {
  // Core utility
  generateRandomDelay,
  sleep,

  // Debouncing functions (for UI interactions)
  debounceWithFixedDelay,

  // Async delay functions (for loading states)
  withMinimumDelay,
  withRandomMinimumDelay,

  // Constants
  UX_DELAY_MIN,
  UX_DELAY_MAX,
  STANDARD_FIXED_DELAY,
  DIALOG_FOCUS_DELAY_MS
}
